import React, { useContext, useEffect, useState } from "react"
import ReactECharts from 'echarts-for-react';
import AppContext from "../../../state/AppContext";
import { useSelector } from "react-redux";
import APIInvoke from "../../../utils/APIInvoke";
import { toast } from 'react-hot-toast';
import { Button, IconButton, LinearProgress, Paper, Tooltip } from "@mui/material";
import { setLoading } from "../../../template/app-actions";
import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PollIcon from '@mui/icons-material/Poll';


const TopCollections = (props) => {

    const navigate = useNavigate();
    const location = useLocation();
    const { appContext } = useContext(AppContext);
    const project = appContext.project

    const [page, setPage] = useState(1);
    const [series, setSeries] = useState(undefined);
    const [counter, setCounter] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [isRunning, setIsRunning] = useState(false);

    // Efecto para actualizar el contador cada 10 segundos
    useEffect(() => {
        const interval = setInterval(() => {
            if (!isRunning) return;
            fetchData();
        }, 10000);

        // Limpieza del intervalo cuando el componente se desmonte
        return () => clearInterval(interval);
    }, [isRunning]);

    useEffect(() => {
        if (loading) return;
        fetchData();
        setPageSize(10)
    }, [project]);

    useEffect(() => {
        fetchData();
    }, [pageSize, page]);

    const fetchData = async () => {
        try {
            setLoading(true);
            let response = await (await APIInvoke.invokeGET(`/projects/${project}/dashboard/events/collections/hourly?page=${page}&pageSize=${pageSize}`)).json();

            response = response.body.map(item => {
                return {
                    ...item,
                    data: item.data.map(d => {
                        return {
                            ...d,
                            _id: {
                                ...d._id,
                                date: DateTime.fromISO(`${d._id.day}T${d._id.hour.toString().padStart(2, '0')}:00:00`, { zone: 'UTC' }).setZone('local')
                            }
                        }
                    })
                }
            })


            setSeries(response);
        } catch (error) {
            console.error(error);
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }

    const handleChartClick = (params, item) => {
        const { name, value, date } = params.data;
        const startDate = DateTime.fromMillis(date.ts);
        const endDate = startDate.plus({ hours: 1 });

        const queryParam = {
            "query": {
                "$and": [
                    {
                        "lastReceivedAt": {
                            "$gte": `ISODate("${startDate.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSSZZZ")}")`
                        }
                    },
                    {
                        "lastReceivedAt": {
                            "$lte": `ISODate("${endDate.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSSZZZ")}")`
                        }
                    }
                ]
            },
            "queryBuilder": {
                "combinator": "and",
                "rules": [
                    {
                        "field": "lastReceivedAt",
                        "operator": ">=",
                        "value": startDate.toFormat("yyyy-MM-dd'T'HH:mm"),
                        "id": "5e00e8bd-c99e-44ac-bb89-3ea3589b23d1"
                    },
                    {
                        "field": "lastReceivedAt",
                        "operator": "<=",
                        "valueSource": "value",
                        "value": endDate.toFormat("yyyy-MM-dd'T'HH:mm"),
                        "id": "1ca020bb-67b5-413a-8148-eae117c9b201"
                    }
                ],
                "id": "4cf14825-d545-47a2-b83f-bb7653e2dabf"
            }
        }

        const queryParam64 = btoa(JSON.stringify(queryParam));

        navigate(`/projects/${project}/collections/${item.collection}?query=${queryParam64}`, { state: { backLink: location.pathname } });
    };

    const getOption = (item) => {
        return {
            tooltip: {
                trigger: 'axis',
                formatter: function (params) {
                    const data = params[0];
                    return `${data.name}<br/>Eventos: ${data.value}`;
                }
            },
            grid: {
                containLabel: false,
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
            },
            xAxis: {
                type: 'category',
                show: true,
                data: item.map(item => item._id.date.hour),
                boundaryGap: true,
                axisLabel: {
                    rotate: 45,
                    fontSize: 6,
                    formatter: function (value) {
                        return `${value.toString().padStart(2, '0')}`;
                    }
                }
            },
            yAxis: {
                type: 'value',
                name: 'Número de Eventos',
                show: false,
                minInterval: 1,
                axisLabel: {
                    formatter: '{value}'
                }
            },
            series: [{
                name: item.collection,
                type: 'bar',
                showBackground: true,
                data: item.map(item => ({
                    value: item.eventCount,
                    name: `${item._id.date.day} ${item._id.date.hour.toString().padStart(2, '0')}:00`,
                    date: item._id.date
                })),
                smooth: true,
                showSymbol: true,
                symbolSize: 6,
                animationEasing: "linear",
                itemStyle: {
                    color: '#22c55e',
                    borderColor: '#22c55e',
                    borderRadius: [3, 3, 0, 0]
                },
                backgroundStyle: {
                    color: 'rgba(180, 180, 180, 0.1)'
                },
                lineStyle: {
                    width: 2
                },
                areaStyle: {
                    opacity: 0.1
                }
            }]
        };
    }

    return (
        <div>
            <Paper className="">
                <div className="relative px-4 bg-gray-100 p-2 border-t border-gray-300 flex flex-row justify-between items-center">
                    <div className="flex flex-row items-center"><PollIcon fontSize="medium" className="text-dark-500"/><span className="pl-2">Top collections (24hrs)</span></div>
                    <Tooltip title={isRunning ? "Stop" : "Start"} className="flex items-center">
                        <IconButton onClick={() => setIsRunning(!isRunning)}>
                            {isRunning
                                ? <StopCircleIcon className={isRunning ? "text-red-500 animate-pulse" : "text-red-500"} fontSize="medium" />
                                : <PlayCircleIcon className="text-green-500" fontSize="medium" />}
                        </IconButton>
                    </Tooltip>
                </div>
                <div className="grid gap-4 p-4">
                    {(series || []).length === 0 && <p className="text-center text-gray-700">No activity in the last 24 hours</p>}
                    {(series || []).map((item, index) => (
                        <div key={index} className="flex flex-row gap-2 items-center">
                            <Tooltip title={`${item.collection} (${item.eventCount})`} className="w-24">
                                <small className="truncate !text-xs">{item.collection} ({item.eventCount})</small>
                            </Tooltip>
                            <ReactECharts
                                option={getOption(item.data)}
                                showLoading={loading}
                                className="!h-8 !w-full !flex-1"
                                onEvents={{
                                    click: (params) => handleChartClick(params, item)
                                }}
                            />
                        </div>
                    ))}
                    <Button variant="contained" onClick={() => setPageSize(pageSize + 10)}>More</Button>
                </div>
            </Paper>
        </div>
    )
}

export default TopCollections;